import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Class as ClassIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  FitnessCenter as FitnessCenterIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { classInstanceAPI } from '../../services/api';
import { ClassInstance, ClassBooking } from '../../types';
import { formatDate, formatTimeString, formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import RegisterMemberToInstanceDialog from './RegisterMemberToInstanceDialog';

const ViewClassInstancePage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Determine referrer for breadcrumbs - check location state or fallback to calendar
  const referrer = (location.state as any)?.from || 'calendar';

  // State management
  const [classInstance, setClassInstance] = useState<ClassInstance | null>(null);
  const [bookings, setBookings] = useState<ClassBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [showUnregisterDialog, setShowUnregisterDialog] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  // Helper function to get breadcrumb path based on referrer
  const getBreadcrumbPath = () => {
    switch (referrer) {
      case 'class-management':
        return { label: 'Class Management', icon: <FitnessCenterIcon />, path: '/class-management' };
      case 'dashboard':
        return { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' };
      case 'calendar':
      default:
        return { label: 'Calendar', icon: <CalendarTodayIcon />, path: '/calendar' };
    }
  };

  const breadcrumbPath = getBreadcrumbPath();

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    instructor: '',
    venue: '',
    capacity: 0,
    sessionDate: '',
    startTime: '',
    endTime: '',
    costOverride: '',
    status: '',
    notes: ''
  });

  // Fetch class instance data
  useEffect(() => {
    if (instanceId) {
      fetchClassInstance();
    }
  }, [instanceId]);

  const fetchClassInstance = async () => {
    if (!instanceId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await classInstanceAPI.getInstance(instanceId);
      const instance = response.data;
      
      setClassInstance(instance);

      // Helper function to convert time string to HH:mm format for time inputs
      const formatTimeForInput = (timeString: string | null | undefined): string => {
        if (!timeString) return '';
        // If time is in HH:mm:ss format, strip the seconds
        // If time is already in HH:mm format, return as is
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
        return timeString;
      };

      // Initialize edit form with current data
      setEditForm({
        name: instance.name || '',
        instructor: instance.instructor || '',
        venue: instance.venue || '',
        capacity: instance.capacity || 0,
        sessionDate: instance.sessionDate.split('T')[0], // Extract YYYY-MM-DD without timezone conversion
        startTime: formatTimeForInput(instance.startTime),
        endTime: formatTimeForInput(instance.endTime),
        costOverride: instance.costOverride?.toString() || '',
        status: instance.status || 'active',
        notes: instance.notes || ''
      });

      // Fetch bookings for this instance
      if (instance.bookings) {
        setBookings(instance.bookings);
      }

    } catch (error: any) {
      console.error('Error fetching class instance:', error);
      setError('Failed to load class instance details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!instanceId || !classInstance) return;

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        name: editForm.name,
        instructor: editForm.instructor,
        venue: editForm.venue,
        capacity: parseInt(editForm.capacity.toString()),
        sessionDate: editForm.sessionDate,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        costOverride: editForm.costOverride ? parseFloat(editForm.costOverride) : null,
        status: editForm.status,
        notes: editForm.notes
      };

      await classInstanceAPI.updateInstance(instanceId, updateData);
      
      // Refresh the data
      await fetchClassInstance();
      setEditMode(false);

    } catch (error: any) {
      console.error('Error updating class instance:', error);
      setError('Failed to update class instance');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form to original values
    if (classInstance) {
      // Helper function to convert time string to HH:mm format for time inputs
      const formatTimeForInput = (timeString: string | null | undefined): string => {
        if (!timeString) return '';
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
        return timeString;
      };

      setEditForm({
        name: classInstance.name || '',
        instructor: classInstance.instructor || '',
        venue: classInstance.venue || '',
        capacity: classInstance.capacity || 0,
        sessionDate: classInstance.sessionDate.split('T')[0], // Extract YYYY-MM-DD without timezone conversion
        startTime: formatTimeForInput(classInstance.startTime),
        endTime: formatTimeForInput(classInstance.endTime),
        costOverride: classInstance.costOverride?.toString() || '',
        status: classInstance.status || 'active',
        notes: classInstance.notes || ''
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'scheduled':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'waitlist':
        return 'warning';
      case 'attended':
        return 'info';
      case 'no_show':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleUnregisterMember = (bookingId: string) => {
    if (!instanceId) return;
    setSelectedBookingId(bookingId);
    setShowUnregisterDialog(true);
  };

  const confirmUnregisterMember = async () => {
    if (!instanceId || !selectedBookingId) return;

    try {
      await classInstanceAPI.unregisterMemberFromInstance(instanceId, selectedBookingId);
      setShowUnregisterDialog(false);
      setSelectedBookingId(null);
      // Refresh the data
      await fetchClassInstance();
    } catch (error: any) {
      console.error('Error unregistering member:', error);
      setError('Failed to unregister member');
      setShowUnregisterDialog(false);
      setSelectedBookingId(null);
    }
  };

  const handleRegistrationSuccess = async () => {
    // Refresh the data after successful registration
    await fetchClassInstance();
  };

  const isInstanceInPast = (): boolean => {
    if (!classInstance) return false;
    
    const sessionDate = new Date(classInstance.sessionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    sessionDate.setHours(0, 0, 0, 0);
    
    // If session date is in the past, it's definitely past
    if (sessionDate < today) {
      return true;
    }
    
    // If session date is today, check the time
    if (sessionDate.getTime() === today.getTime()) {
      const now = new Date();
      const [hours, minutes] = (classInstance.startTime || '00:00').split(':').map(Number);
      const sessionTime = new Date();
      sessionTime.setHours(hours, minutes, 0, 0);
      
      return now > sessionTime;
    }
    
    return false;
  };

  const handleCancelInstance = async () => {
    if (!instanceId || !classInstance) return;

    try {
      setCancelling(true);
      setError(null);

      const response = await classInstanceAPI.cancelInstance(instanceId);
      
      // Close dialog
      setShowCancelDialog(false);
      
      // Refresh the data
      await fetchClassInstance();
      
      // Show success message with details
      const data = response.data;
      if (data.affectedMembers > 0) {
        alert(`Class instance cancelled successfully.\n\n${data.affectedMembers} member(s) will be notified.\n${data.cancelledTransactions || 0} pending transaction(s) cancelled.`);
      }
    } catch (error: any) {
      console.error('Error cancelling class instance:', error);
      setError(error.response?.data?.message || 'Failed to cancel class instance');
      setShowCancelDialog(false);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!classInstance) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Class instance not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to="/dashboard"
          color="inherit"
          startIcon={<HomeIcon />}
          sx={{ textTransform: 'none' }}
        >
          Dashboard
        </Button>
        {referrer !== 'dashboard' && (
          <Button
            component={RouterLink}
            to={breadcrumbPath.path}
            color="inherit"
            startIcon={breadcrumbPath.icon}
            sx={{ textTransform: 'none' }}
          >
            {breadcrumbPath.label}
          </Button>
        )}
        {classInstance.parentClass && (
          <Button
            component={RouterLink}
            to={`/classes/${classInstance.parentClassId}`}
            state={{ from: referrer }}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            {classInstance.parentClass.name}
          </Button>
        )}
        <Typography color="text.primary">Instance Details</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <ClassIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {classInstance.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Class Instance • {formatDate(classInstance.sessionDate)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!editMode ? (
            <>
              {/* {classInstance.status !== 'CANCELLED' && !isInstanceInPast() && (user?.role !== 'member') && ( */}
              {/* Not doing a check for past classes as we may need to genuinely create an older class and it's confusing for users to not see the button in some cases */}
              {classInstance.status !== 'CANCELLED' && (user?.role !== 'member') && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowCancelDialog(true)}
                >
                  Cancel Instance
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
                disabled={user?.role == 'member'}
              >
                Edit Instance
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Class Instance Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ClassIcon sx={{ mr: 1 }} />
              Instance Details
              <Chip 
                label={classInstance.status} 
                color={getStatusColor(classInstance.status)}
                size="small"
                sx={{ ml: 2 }}
              />
            </Typography>
            
            {editMode ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Class Name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Instructor"
                    value={editForm.instructor}
                    onChange={(e) => setEditForm({ ...editForm, instructor: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Venue"
                    value={editForm.venue}
                    onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Capacity"
                    type="number"
                    value={editForm.capacity}
                    onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Session Date"
                    type="date"
                    value={editForm.sessionDate}
                    onChange={(e) => setEditForm({ ...editForm, sessionDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Cost Override"
                    type="number"
                    value={editForm.costOverride}
                    onChange={(e) => setEditForm({ ...editForm, costOverride: e.target.value })}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editForm.status}
                      label="Status"
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="scheduled">Scheduled</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Instructor</Typography>
                      <Typography variant="body1">{classInstance.instructor}</Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Venue</Typography>
                      <Typography variant="body1">{classInstance.venue}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Session Date</Typography>
                      <Typography variant="body1">{formatDate(classInstance.sessionDate)}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Time</Typography>
                      <Typography variant="body1">
                        {formatTimeString(classInstance.startTime)} - {formatTimeString(classInstance.endTime)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Capacity</Typography>
                      <Typography variant="body1">
                        {classInstance.enrolledCount || 0} / {classInstance.capacity} enrolled
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {classInstance.costOverride && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Cost Override</Typography>
                        <Typography variant="body1">${classInstance.costOverride}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {classInstance.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Notes</Typography>
                      <Typography variant="body1">{classInstance.notes}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>

          {/* Parent Class Information */}
          {classInstance.parentClass && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <FitnessCenterIcon sx={{ mr: 1 }} />
                Parent Class Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Class Name</Typography>
                  <Typography variant="body1">{classInstance.parentClass.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Type & Level</Typography>
                  <Typography variant="body1">
                    {classInstance.parentClass.type} - {classInstance.parentClass.level}
                  </Typography>
                </Box>
                {classInstance.parentClass.description && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{classInstance.parentClass.description}</Typography>
                  </Box>
                )}
                <Box>
                  <Button
                    component={RouterLink}
                    to={`/classes/${classInstance.parentClassId}`}
                    variant="outlined"
                    size="small"
                  >
                    View Parent Class
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Capacity Status */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Capacity Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3" color="primary">
                    {classInstance.enrolledCount || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {classInstance.capacity} spots filled
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Available: {(classInstance.capacity - (classInstance.enrolledCount || 0))}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(((classInstance.enrolledCount || 0) / classInstance.capacity) * 100)}% full
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(((classInstance.enrolledCount || 0) / classInstance.capacity) * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: ((classInstance.enrolledCount || 0) / classInstance.capacity) >= 0.8 ? 'error.main' : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                {classInstance.isWaitlistAvailable && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Waitlist available when full
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Bookings List */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    Bookings ({bookings.length})
                  </Typography>
                  {(user?.role !== 'member') && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonIcon />}
                      onClick={() => setShowRegisterDialog(true)}
                    >
                      + Register
                    </Button>
                  )}
                </Box>
                
                {bookings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No bookings for this instance
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Member</TableCell>
                          <TableCell>Status</TableCell>
                          {(user?.role !== 'member' ) && (
                            <TableCell align="right">Actions</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.875rem' }}>
                                  {(booking.memberName || booking.userName)?.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">{booking.memberName || booking.userName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {booking.memberEmail || booking.userEmail}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={booking.status}
                                color={getBookingStatusColor(booking.status)}
                                size="small"
                              />
                            </TableCell>
                            {(user?.role !== 'member') && (
                              <TableCell align="right">
                                {booking.status !== 'cancelled' && (
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleUnregisterMember(booking.id)}
                                  >
                                    Unregister
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Registration Dialog */}
      <RegisterMemberToInstanceDialog
        open={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        classInstance={classInstance}
        onSuccess={handleRegistrationSuccess}
      />

      {/* Cancel Instance Confirmation Dialog */}
      <Dialog 
        open={showCancelDialog} 
        onClose={() => !cancelling && setShowCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Class Instance</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel this class instance?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>{classInstance?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(classInstance?.sessionDate || '')} • {formatTimeString(classInstance?.startTime || '')} - {formatTimeString(classInstance?.endTime || '')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {classInstance?.venue}
            </Typography>
          </Box>
          {bookings.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{bookings.length} member(s)</strong> are registered for this class and will be notified of the cancellation.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Any pending transaction fees will be automatically cancelled.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowCancelDialog(false)}
            disabled={cancelling}
          >
            Keep Instance
          </Button>
          <Button
            onClick={handleCancelInstance}
            variant="contained"
            color="error"
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Instance'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unregister Member Confirmation Dialog */}
      <Dialog open={showUnregisterDialog} disableEscapeKeyDown={true}>
        <DialogTitle>Unregister Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unregister this member from the class instance?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowUnregisterDialog(false);
            setSelectedBookingId(null);
          }}>Cancel</Button>
          <Button onClick={confirmUnregisterMember} color="error" variant="contained">
            Unregister
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewClassInstancePage;